// LineasPanelXL (contabilidad) - Sprint 44 Fase 3 Vista 8.
// Panel de lineas DR/CR con balance en vivo. XLMoneyInput para montos.
// Reusa contabilidadService legacy. Cero backend touch.
import { useCallback, useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import * as svc from '../services/contabilidadService';
import CuentaPickerXL from './CuentaPickerXL';
import XLMoneyInput from '../../../components/xl/XLMoneyInput';
import XLSkeleton from '../../../components/xl/XLSkeleton';
import { fmt } from '../utils';

const CSS_ID = 'xl-cont-lineas-css';
const CSS = `
.xl-cl { display: flex; flex-direction: column; gap: 16px; font-family: var(--xl-font-sans) }
.xl-cl__addbar {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 14px 16px;
}
.xl-cl__addbar-lbl {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary); margin-bottom: 6px;
}
.xl-cl__table-wrap {
  overflow-x: auto;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: var(--xl-color-surface);
  box-shadow: var(--xl-shadow-sm);
}
.xl-cl__table { width: 100%; min-width: 640px; border-collapse: collapse; font-size: 13px }
.xl-cl__table thead th, .xl-cl__table tfoot td {
  background: var(--xl-color-bg);
  color: var(--xl-color-text-secondary);
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid var(--xl-color-border);
  white-space: nowrap;
}
.xl-cl__table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--xl-color-border);
  color: var(--xl-color-text);
  vertical-align: middle;
}
.xl-cl__table tr:last-child td { border-bottom: 0 }
.xl-cl__cuenta-code { font-family: var(--xl-font-mono); font-weight: 600; font-size: 12px }
.xl-cl__cuenta-name { font-size: 11px; color: var(--xl-color-text-muted); margin-top: 2px }
.xl-cl__desc {
  width: 100%;
  font: 400 12px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  padding: 6px 8px;
  box-sizing: border-box;
}
.xl-cl__desc:focus {
  outline: none; border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
}
[data-xl-theme="dark"] .xl-cl__desc:focus { box-shadow: 0 0 0 3px rgba(96,165,250,0.28) }
.xl-cl__del {
  background: transparent; border: none; cursor: pointer;
  color: var(--xl-color-danger);
  padding: 4px 8px; border-radius: var(--xl-radius-sm);
  font-size: 13px;
}
.xl-cl__del:hover { background: rgba(220,38,38,0.08) }
.xl-cl__empty { padding: 28px 16px; text-align: center; color: var(--xl-color-text-muted); font-size: 13px }
.xl-cl__totals {
  font-family: var(--xl-font-mono); text-transform: none !important;
  font-size: 13px; color: var(--xl-color-text);
  letter-spacing: 0;
}
.xl-cl__totals--ok  { color: var(--xl-color-success) }
.xl-cl__totals--bad { color: var(--xl-color-danger) }
.xl-td-right { text-align: right }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
`;

export default function LineasPanelXL({ asientoId, isEditable, onChanged }) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!asientoId) return;
    setLoading(true);
    try { setItems(await svc.listLineas(asientoId) || []); }
    finally { setLoading(false); }
  }, [asientoId]);

  useEffect(() => { load(); }, [load]);

  const notify = () => { if (onChanged) onChanged(); };

  const onPickCuenta = async (c) => {
    if (!isEditable) return;
    try {
      await svc.createLinea(asientoId, {
        cuenta_id: c.id, debito: 0, credito: 0, descripcion: '',
      });
      await load(); notify();
    } catch (e) { alert((e && e.message) || 'Error'); }
  };

  const onEdit = async (l, field, value) => {
    if (!isEditable) return;
    try {
      const v = Number(value) || 0;
      const payload = { [field]: v };
      if (field === 'debito' && v > 0) payload.credito = 0;
      if (field === 'credito' && v > 0) payload.debito = 0;
      await svc.updateLinea(l.id, payload);
      await load(); notify();
    } catch (e) { alert((e && e.message) || 'Error'); }
  };

  const onEditDesc = async (l, value) => {
    if (!isEditable) return;
    try { await svc.updateLinea(l.id, { descripcion: value }); await load(); notify(); }
    catch (e) { alert((e && e.message) || 'Error'); }
  };

  const onDelete = async (l) => {
    if (!isEditable) return;
    if (!window.confirm('Eliminar linea?')) return;
    try { await svc.deleteLinea(l.id); await load(); notify(); }
    catch (e) { alert((e && e.message) || 'Error'); }
  };

  const tot_d = items.reduce((a, l) => a + Number(l.debito || 0), 0);
  const tot_c = items.reduce((a, l) => a + Number(l.credito || 0), 0);
  const dif = Math.round((tot_d - tot_c) * 100) / 100;
  const balanceado = Math.abs(dif) < 0.01;
  const totalsCls = 'xl-cl__totals ' + (balanceado ? 'xl-cl__totals--ok' : 'xl-cl__totals--bad');

  return (
    <div className="xl-cl">
      {isEditable && (
        <div className="xl-cl__addbar">
          <div className="xl-cl__addbar-lbl">Agregar cuenta</div>
          <CuentaPickerXL onPick={onPickCuenta} />
        </div>
      )}
      <div className="xl-cl__table-wrap">
        <table className="xl-cl__table">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>Descripcion</th>
              <th className="xl-td-right">Debito</th>
              <th className="xl-td-right">Credito</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 2 }).map((_, i) => (
              <tr key={'sk-' + i}>
                <td colSpan={5}><XLSkeleton variant="line" width="80%" height={18} /></td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="xl-cl__empty">Sin lineas.</td></tr>
            )}
            {!loading && items.map((l) => (
              <tr key={l.id}>
                <td>
                  <div className="xl-cl__cuenta-code">{l.cuenta && l.cuenta.codigo}</div>
                  <div className="xl-cl__cuenta-name">{l.cuenta && l.cuenta.nombre}</div>
                </td>
                <td>
                  <input className="xl-cl__desc" disabled={!isEditable}
                    defaultValue={l.descripcion || ''}
                    onBlur={(e) => onEditDesc(l, e.target.value)} />
                </td>
                <td className="xl-td-right">
                  <XLMoneyInput value={l.debito || 0}
                    onChange={(n) => onEdit(l, 'debito', n)}
                    disabled={!isEditable} />
                </td>
                <td className="xl-td-right">
                  <XLMoneyInput value={l.credito || 0}
                    onChange={(n) => onEdit(l, 'credito', n)}
                    disabled={!isEditable} />
                </td>
                <td className="xl-td-right">
                  {isEditable && (
                    <button type="button" className="xl-cl__del"
                      onClick={() => onDelete(l)} aria-label="Eliminar linea">X</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="xl-td-right">TOTALES</td>
              <td className="xl-td-right xl-mono">{fmt(tot_d)}</td>
              <td className="xl-td-right xl-mono">{fmt(tot_c)}</td>
              <td />
            </tr>
            <tr>
              <td colSpan={2} className="xl-td-right">DIFERENCIA</td>
              <td colSpan={2} className={'xl-td-right ' + totalsCls}>
                {fmt(dif)} {balanceado ? '\u2713 balanceado' : '\u26A0 desbalanceado'}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
